define((require) => {
    "use strict";

    const {
        first, clone, isFunction, extend
    } = require("underscore");
    const Browser                            = require("shared/services/Browser");
    const template                           = require("ui/experience/widgets/WidgetToolboxView.tpl");
    const WidgetTypes                        = require("shared/ui/experience/widgets/WidgetTypes");
    const AbstractWidgetPropertiesView       = require("ui/experience/widgets/AbstractWidgetPropertiesView");
    const MultipleSelectionPropertiesView    = require("ui/experience/widgets/MultipleSelectionPropertiesView");
    const BackdropView                       = require("ui/experience/BackdropView");
    const Lightbox                           = require("shared/ui/common/Lightbox");
    const ReactBridge                        = require("backbone.react-bridge");
    const WidgetToolboxComponent             = require("ui/experience/widgets/toolbox/WidgetToolboxComponent").default;
    const ExperienceSettingsComponent        = require("ui/experience/widgets/toolbox/ExperienceSettingsComponent").default;
    const WidgetTabOrderContainer            = require("ui/experience/widgets/toolbox/WidgetTabOrderContainer").default;
    const WidgetLayerOrderContainer          = require("ui/experience/widgets/toolbox/WidgetLayerOrderContainer").default;
    const Keyboard                           = require("services/Keyboard");
    const LoggedInUserState                  = require("shared/ui/user/LoggedInUserState");
    const {TAB_ORDER_ENABLED}                = require("ui/experience/widgets/toolbox/TabOrder/TabOrderDndTypes");
    const EditorEventFilters                 = require("ui/editor/EditorEventFilters").default;
    const EditorConstants                    = require("editor/ui/experience/editor/EditorConstants");
    const {mapWidgetMetaData}                = require("ui/editor/SceneEditorHelpers");


    const EXPAND_PROPERTY = "expandToolbox";

    /**
     * The widget toolbox view, displaying widgets that can be dragged onto a scene.
     *
     * @class WidgetToolboxView
     * @constructor
     *
     * @cfg {EditorViewModel} model The editor view model.
     */
    return AbstractWidgetPropertiesView.extend({

        expandProperty: EXPAND_PROPERTY,

        template,

        _widgetIcons: [],
        _widgetTypes: [],
        templateHelpers() {
            const key = Browser.os.macOS ? "\u2318" : "Ctrl";

            return {
                button: key,
                spectatorMode: this.viewScope.spectatorMode
            };
        },

        events: {
            "dragstart .widget-icon": "_dragStartHandler",
            "click .widget-icon": "_clickHandler"
        },

        ui: {
            toolboxUi: ".widget-toolbox-container"
        },

        regions: {
            widgetPropertyRegion: ".widget-controls",
            toolboxContainer: ".widget-toolbox-container",
            experienceSettingsContainer: ".experience-settings-container",
            behaviorProperties: ".behaviors-properties",
            widgetTabOrderContainer: ".widget-tab-order-container",
            widgetLayerOrderContainer: ".widget-layer-order-container"
        },

        modelEvents: {
            "change:currentLayer": "_renderWidgetIcons"
        },

        initialize(options) {
            this.__super(options);
            this.listenTo(options.model.selection, "change:widgets", this._widgetSelectHandler);
            this.listenTo(options.model.get("experience"), "change:emailModeEnabled", this._updateWidgetIcons);
            this.listenTo(options.model.get("experience"), "change:emailModeEnabled", this.onShow);
            this.listenTo(Keyboard.Down, Keyboard.KeyCode.ESCAPE, () => {
                this.viewScope.clearCursorWidgetType();
            });
        },

        onRender() {
            this.__super();
            this._widgetTypes = this._getWidgetTypes();
        },

        onShow() {
            this._renderWidgetIcons();
            this._renderExperienceSettings();
            this.behaviorProperties.show(this._setBehaviorAction({model: this.model}));
            this._renderWidgetTabOrder();
            this._renderWidgetLayerOrder();
        },

        _updateWidgetIcons() {
            this._widgetTypes = this._getWidgetTypes();
            this._renderWidgetIcons();
        },

        _dragStartHandler(event) {
            if (!this.viewScope.spectatorMode) {
                // data-widget-type attribute on both image (target) and link (currentTarget)!
                const data = {
                    defaultActionId: event.target.getAttribute("data-default-action-id"),
                    icon: event.target.getAttribute("data-icon"),
                    type: event.target.getAttribute("data-widget-type")
                };

                const dataTransfer = event.originalEvent.dataTransfer;
                // IE9+ only supports "Text" as the mimetype. We have seen problems in Chromium
                // where this stopped working, renaming mime type to "text/html" fixed it. This
                // will not work in IE, so we need to differentiate if this becomes a problem in
                // Chrome as well. For now we go with Text as it is only Chromium that fails.
                // Update SceneDropHandler as well if you change this.  ataTransfer.setData() has
                // been replaced by dataTransfer.items.add() in Edge:
                // http://www.meetup.com/Copenhagen-Tech-Polyglots/events/230314018/.
                dataTransfer.setData("Text", JSON.stringify(data));

                // This is used to prevent the appearance of url when dragging a widget
                if (dataTransfer.setDragImage) {
                    dataTransfer.setDragImage(event.target, 0, 0);
                }
            } else {
                event.preventDefault();
            }
        },

        _clickHandler(event) {
            if (event.currentTarget.dataset.suspended) {
                return false;
            }
            const widgetType = event.currentTarget.dataset.widgetType;
            const icon = event.currentTarget.dataset.icon;
            this.viewScope.setCursorWidgetType(widgetType, icon);
            event.preventDefault();
        },

        _onClickBackdrop(event) {
            event.preventDefault();

            if (this.viewScope.spectatorMode || event.currentTarget.dataset.suspended) {
                return;
            }
            const viewModel = this.viewScope.viewModel;

            this._showLightbox({
                view: new BackdropView({
                    model: viewModel.get("experience").get("backdrop"),
                    viewModel,
                    integrationsCollection: LoggedInUserState.get("integrations")
                }),
                css: {width: 640},
                experience: viewModel.get("experience"),
                props: {
                    modalType: "BackdropOptions",
                    triggerOrigin: "BackdropOptionsModal",
                    sceneNumber: viewModel.get("sceneNumber")
                }
            });
        },

        _showLightbox({
            view, css, experience, props
        }) {
            const lightboxOptions = {
                win: false,
                css,
                editorEventProperties: extend({
                    experienceId: experience.get("id"),
                    experienceVersion: experience.get("version"),
                    experiencePushedVersion: experience.get("pushedVersion")
                }, props)
            };

            new Lightbox(view, lightboxOptions, this).open();
        },


        /**
         * Updates the right sidebar to show corresponding properties editing
         */
        _widgetSelectHandler(selection) {
            const propertyView = this._getPropertyView(selection);

            this._renderExperienceSettings();
            this._renderWidgetTabOrder();
            this._renderWidgetLayerOrder();

            if (propertyView) {
                this.widgetPropertyRegion.show(propertyView);
            } else {
                this.widgetPropertyRegion.reset();
            }
        },

        /**
         * Works with bootstrap's "collapse".
         *
         * @param {JQuery<HTMLElement>} $el Element to collapse
         * @param {String} hide             Can be 'hide' or 'show'.
         */
        _collapse($el, hide) {
            $el.collapse(hide);
        },

        /**
         * Return the widget icon path, e.g. "generic-product" => "widget-icons/generic-product-widget-icon.svg".
         */
        _getWidgetIcon(iconId) {
            return (`widget-icons/${iconId}-widget-icon.svg`);
        },

        /**
         * Returns a new ordered array of widget types relevant to users of the current company.
         *
         * @return {WidgetTypes} An array of <tt>WidgetTypes</tt> constants
         */
        _getWidgetTypes() {
            // Start with the list of widgets that declares an ordering and currently enabled.
            const widgetTypes = [];
            const {prototypeWidgetCollection} = this.options;

            WidgetTypes.each((descriptor) => {
                if (descriptor.order) {
                    // If defined, call "enabled()" on the type descriptor - a static function is widget types
                    // are by design "visually singletons"...
                    if (!isFunction(descriptor.enabled) || descriptor.enabled()) {
                        if (descriptor.id === "text-enhanced") {
                            const textWidget = WidgetTypes.lookup("text");
                            if (!textWidget.enabled()) {
                                descriptor.icon = textWidget.icon;
                            }
                        }
                        if (isFunction(descriptor.suspend)) {
                            descriptor.suspend(this.model);
                        }
                        widgetTypes.push(descriptor);
                    }
                }
            });

            // Sort according to visual order.
            widgetTypes.sort((a, b) => (a.order - b.order));

            // Add the widgets defined for the current company.
            prototypeWidgetCollection.each((prototypeWidget) => {
                const ownedWidget = prototypeWidget.get("widget");

                if (ownedWidget.get("visible")) {
                    // TODO: if we persist "order", we can simply add all prototype widgets and do a sort of all
                    //       widgets, for example to fine tune the order of different product widgets!
                    const widgetType = WidgetTypes.lookup(ownedWidget.get("type")); // e.g. "ProductWidget"
                    // Clone the type, so that it's possible to configure multiple widgets of the same type.
                    const widgetTypeClone = clone(widgetType);
                    widgetTypeClone.defaultActionId = ownedWidget.get("defaultActionId");
                    widgetTypeClone.icon = ownedWidget.get("icon");
                    // Currently all owned widgets have a special placeholder icon.
                    widgetTypeClone.tooltip = ownedWidget.get("tooltip");

                    widgetTypes.push(widgetTypeClone);
                }
            });

            return widgetTypes;
        },

        _shouldExpandOnRender() {
            return true;
        },

        /**
         * @private
         */
        _renderWidgetIcons() {
            this.ui.toolboxUi.empty();

            const toolboxReactComponent = ReactBridge.viewFromComponent(WidgetToolboxComponent, {
                props: {
                    widgetTypes: this._widgetTypes
                }
            });
            this.toolboxContainer.show(toolboxReactComponent);
        },

        _stripHtml(html) {
            var tmp = document.createElement("DIV");
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || "";
        },

        onSelectReorder(uuid) {
            this.model.get("experience").set("selectedWidgetId", uuid);
        },

        onCancelReorder(uuid) {
            if (uuid !== this.model.get("experience").get("selectedWidgetId")) {
                // Do not fire cancel event
                // Because it's has been obtained after new widget has been selected
                return;
            }
            this.model.get("experience").set("selectedWidgetId", null);
        },

        onItemRemove(id, columnId, index) {
            const activeLayer = this.viewScope.viewModel.get("activeLayerSceneModel");
            let source;
            let destination;
            if (columnId === TAB_ORDER_ENABLED) {
                source = "widgetTabOrder";
                destination = "removedWidgetTabOrder";
            } else {
                source = "removedWidgetTabOrder";
                destination = "widgetTabOrder";
            }

            const tabOrderSource = [...activeLayer.get(source)];
            const tabOrderDestination = [...activeLayer.get(destination)];
            const insertIndex = index || tabOrderDestination.length;

            const filteredTabOrderSource = tabOrderSource.filter((uuid) => uuid !== id);
            tabOrderDestination.splice(insertIndex, 0, id);

            activeLayer.set(destination, tabOrderDestination);
            activeLayer.set(source, filteredTabOrderSource);

            this.model.get("experience").set("selectedWidgetId", null);
        },

        onEndReorder(columnId, oldIndex, newIndex) {
            if (oldIndex !== undefined) {
                const activeLayer = this.viewScope.viewModel.get("activeLayerSceneModel");
                const source = columnId === TAB_ORDER_ENABLED ? "widgetTabOrder" : "removedWidgetTabOrder";

                const newWidgets = activeLayer.get(source + "MetaData");
                const dragWidget = newWidgets.splice(oldIndex, 1)[0];
                newWidgets.splice(newIndex, 0, dragWidget);

                activeLayer.set(source, newWidgets.map((widget) => widget.id), {cause: EditorEventFilters.SUPERFICIAL});
            }
            this.model.get("experience").set("selectedWidgetId", null);
        },

        _renderExperienceSettings() {
            const scopedGetProps = ({model}) => {
                const backdropModel = model.get("backdrop");
                let groupId;
                if (model.get("group")) {
                    groupId = model.get("group").resourceId;
                }
                return {
                    experienceId: model.get("id"),
                    isEmailMode: model.get("emailModeEnabled"),
                    groupId,
                    height: model.get("height"),
                    width: model.get("width"),
                    autoPlayDelay: Math.round(model.get("autoPlayDelay") / 1000),
                    autoPlayTransition: model.get("transition"),
                    language: model.get("language"),
                    widgetSelected: !this.viewScope.viewModel.selection.isEmpty(),
                    backdrop: {
                        type: backdropModel.get("type"),
                        color: backdropModel.get("color")
                    },
                    setExperienceHeight: (height) => {
                        model.set({height: Math.max(1, height)}, {render: false});
                    },
                    setExperienceWidth: (width) => {
                        model.set({width: Math.max(1, width)}, {render: false});
                    },
                    setExperienceAutoPlayDelay: (delaySeconds) => {
                        model.set({autoPlayDelay: delaySeconds * 1000}, {render: false});
                    },
                    setExperienceAutoPlayTransition: (transition) => {
                        model.set({transition}, {render: false});
                    },
                    setLanguage: (language) => {
                        model.set({language}, {render: false});
                    }
                };
            };
            const experienceSettingsView = ReactBridge.viewFromComponent(ExperienceSettingsComponent, {
                model: this.model.get("experience"),
                observe: {
                    model: "change:id change:height change:width change:autoPlayDelay change:transition change:language change:backdrop"
                },
                props: {
                    // I'm not sure if this will update if the value changes since we don't observe this model.
                    spectatorMode: this.viewScope.spectatorMode,
                    changeBackdrop: (event) => {
                        this._onClickBackdrop(event);
                    }
                },
                getProps: scopedGetProps
            });

            this.experienceSettingsContainer.show(experienceSettingsView);
        },

        _renderWidgetTabOrder() {
            // Do not render tabOrder, if flag hasn't set
            const isTabOrderEnabled = LoggedInUserState.get("companySettings").get("adaTabOrderEnabled");

            const scopedGetProps = ({model}) => {
                const scene = model.get("activeLayerSceneModel");
                const emailModeEnabled = model.get("experience").get("emailModeEnabled");
                return {
                    widgets: isTabOrderEnabled ? scene.get("widgetTabOrderMetaData") : null,
                    removedWidgets: isTabOrderEnabled ? scene.get("removedWidgetTabOrderMetaData") : null,
                    widgetSelected: !this.viewScope.viewModel.selection.isEmpty(),
                    orderEnabled: isTabOrderEnabled,
                    selectedId: model.get("experience").get("selectedWidgetId"),
                    emailModeEnabled: emailModeEnabled
                };
            };

            if (isTabOrderEnabled) {
                const widgetTabOrderContainerView = ReactBridge.viewFromComponent(WidgetTabOrderContainer, {
                    // This.model is a EditorViewModel
                    model: this.model,
                    observe: {
                        model: "change:scene change:currentLayer nestedWidgetTabOrderMetaDataChange change:emailModeEnabled"
                    },
                    props: {
                        // I'm not sure if this will update if the value changes since we don't observe this model.
                        spectatorMode: this.viewScope.spectatorMode,
                        onDragEnd: this.onEndReorder.bind(this),
                        onItemRemove: this.onItemRemove.bind(this),
                        onDragStart: this.onSelectReorder.bind(this),
                        onDragCancel: this.onCancelReorder.bind(this)
                    },
                    getProps: scopedGetProps
                });


                this.widgetTabOrderContainer.show(widgetTabOrderContainerView);
            }
        },

        _renderWidgetLayerOrder() {
            const scopedGetProps = ({model}) => {
                const activeLayer = model.get("activeLayerSceneModel");
                const widgets = [...activeLayer.get("widgets").models]
                    // When widgets are cloned they don't have a UUID yet.
                    .filter((widget) => widget.has("uuid"))
                    // we must to render widgets' models in the reversed order in order to have
                    // widget with the highest z-index at the top of the "Widget Layer Order" list
                    .reverse()
                    .map((widget) => mapWidgetMetaData({widget, addActions: true}));

                return {
                    selectedId: model.get("experience").get("selectedWidgetId"),
                    widgets,
                    widgetSelected: !this.viewScope.viewModel.selection.isEmpty()
                };
            };

            const widgetLayerOrderContainerView = ReactBridge.viewFromComponent(WidgetLayerOrderContainer, {
                model: this.model,
                observe: {
                    model: "change:scene change:currentLayer nestedWidgetTabOrderMetaDataChange onSelectedWidgetIdChange onLockingChange"
                },
                props: {
                    onDragEnd: this.onLayerOrderDragEnd.bind(this),
                    onDragStart: this.onLayerOrderDragStart.bind(this),
                    onDragCancel: this.onLayerOrderDragCancel.bind(this),
                    onDragRelease: this.onLayerOrderDragRelease.bind(this),
                    onActionClick: this.onActionClick.bind(this),
                    onLayerChanging: this.onLayerChanging.bind(this)
                },
                getProps: scopedGetProps
            });

            this.widgetLayerOrderContainer.show(widgetLayerOrderContainerView);
        },

        // before widget is locked it should be unselected
        removeWidgetFromSelection(currentWidget) {
            const selection = this.viewScope.viewModel.selection;
            const isInSelection = selection.get("widgets").find((widget) => widget.model.get("uuid") === currentWidget.get("uuid"));

            currentWidget.set("isSelected", false);

            if (isInSelection) {
                if (selection.get("widgets").length === 1) {
                    selection.clear();
                } else {
                    const selectedWidgets = selection.get("widgets").filter((widget) => widget.model !== currentWidget);
                    selection.select(selectedWidgets);
                }
            }
        },

        lockWidget({widget, lock}) {
            const isLocked = widget.get("isLocked");
            if (!isLocked) {
                this.removeWidgetFromSelection(widget);
            }
            widget.set("isLocked", lock ? true : !isLocked);
        },

        onActionClick(uuid, actionType) {
            switch (actionType) {
                case "lock":
                    this.onLockLayerShift({uuid});
                    break;
                case "hide":
                    this.onLockLayerShift({uuid, isHidingAction: true});
                    break;
                default:
            }
        },

        onLockLayerShift({uuid, isHidingAction}) {
            const activeLayer = this.model.get("activeLayerSceneModel");
            const currentWidget = activeLayer.get("widgets").find((widget) => widget.get("uuid") === uuid);

            if (isHidingAction) {
                const isHidden = currentWidget.get("isEditorHidden");
                if (!isHidden) {
                    this.lockWidget({widget: currentWidget, lock: true});
                }
                currentWidget.set("isEditorHidden", !isHidden);
            } else {
                this.lockWidget({widget: currentWidget});
            }

            this.model.trigger("onLockingChange");
        },

        onLayerOrderDragStart(uuid) {
            this.setSelectedWidgetId(uuid);
        },

        onLayerOrderDragRelease(uuid) {
            this.addWidgetToSelection(uuid);
        },

        addWidgetToSelection(uuid) {
            const scene = this.model.get("activeLayerSceneModel");
            const widgetsModels = scene.get("widgets").models;
            const widget = widgetsModels.find((w) => w.get("uuid") === uuid);
            scene.trigger("selectWidgets", [widget], true);

            widget.trigger("forceWidgetUpdate");
        },

        onLayerOrderDragEnd(oldIndex, newIndex) {
            if (oldIndex !== newIndex) {
                const scene = this.model.get("activeLayerSceneModel");
                const widgetsModels = scene.get("widgets").models;
                const widgetModel = [...widgetsModels].reverse()[oldIndex];
                const siblingWidgets = widgetModel.getGroupSiblings(this.viewScope.viewModel.get("scene").get("widgets"));

                scene.orderWidget({
                    widgetModel,
                    to: EditorConstants.BRING_TO_TARGET.value,
                    siblingWidgets,
                    // as we render widgets in a reversed order, we have to calculate appropriate new index
                    // in order to maintain correct order of widgets' models
                    newIndex: widgetsModels.length - 1 - newIndex
                });
            }

            this.model.get("experience").set("selectedWidgetId", null);
        },

        onLayerOrderDragCancel() {
            this.model.get("experience").set("selectedWidgetId", null);
        },

        onLayerChanging(uuid) {
            this.setSelectedWidgetId(uuid);
            this.addWidgetToSelection(uuid);
        },

        setSelectedWidgetId(uuid) {
            this.model.get("experience").set("selectedWidgetId", uuid);
            this.model.trigger("onSelectedWidgetIdChange");
        },

        /**
         * Get proper view instance for the given selection.
         * For empty selection returns `null`.
         * For selection of multiple widgets returns new instance of `MultipleSelectionPropertiesView`.
         * For the single widget selection returns apropriate ancestor of `AbstractWidgetPropertiesView`.
         *
         * @param {WidgetSelectionViewModel} selection
         * @returns {AbstractWidgetPropertiesView} apropriate widget properties view instance
         */
        _getPropertyView(selection) {
            if (selection.isMultiple()) {
                return new MultipleSelectionPropertiesView({model: selection});
            }

            if (selection.isSingle()) {
                const widgetView = first(selection.get("widgets"));
                const widgetModel = widgetView.model;
                const widgetDescriptor = WidgetTypes.lookup(widgetModel.get("type"));
                return new widgetDescriptor.propertiesViewClass({
                    model: widgetModel,
                    widgetView
                });
            }

            return null;
        }

    }, {
        EXPAND_PROPERTY
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/WidgetToolboxView.js